// ТЭП v2 (решение владельца 2026-07-03) — реестр показателей объекта:
// формируется из ПЗ (таблица ТЭП) + требований ТЗ/ТУ, НЕ из матрицы по типам.
// Сервисный блок валидации нарезки владельцем — тот же паттерн, что
// TaskRequirementsPanel (свёрнут, исключение строк, переизвлечение).
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Gauge, RefreshCw, EyeOff, Eye, ChevronDown, ChevronRight } from 'lucide-react';
import { api } from '@/api/client';
import type { Project } from '@/types/api';
import { canRunCheck, runBlockReason, tariffErrorMessage, tariffStateFromProject } from '@/lib/tariff';
import { PayGateBanner } from '@/components/PayGateBanner';

export interface TepRegItem {
  id: string;
  name: string;
  unit: string | null;
  declared_value: string | null;
  req_value: string | null;
  req_op: string | null;
  req_source: string | null;
  req_item_no: string | null;
  excluded: boolean;
}

export interface TepRegistryResponse {
  has_result: boolean;
  run_id?: string;
  status?: string;
  error?: string | null;
  finished_at?: string | null;
  items: TepRegItem[];
}

const OP_LABEL: Record<string, string> = { '>=': 'не менее', '<=': 'не более', '=': '' };

export function TepRegistryPanel({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [tariffErr, setTariffErr] = useState<string | null>(null);
  const { data } = useQuery<TepRegistryResponse>({
    queryKey: ['tep-registry', projectId],
    queryFn: () => api.get<TepRegistryResponse>(`/projects/${projectId}/tep-registry`),
    refetchInterval: (q) => (q.state.data?.status === 'running' ? 2500 : false),
  });

  // 15.E Reading B: тариф проекта — per-check гейт кнопки формирования реестра ТЭП.
  const projectQ = useQuery<Project>({
    queryKey: ['project', projectId],
    queryFn: () => api.get<Project>(`/projects/${projectId}`),
    staleTime: 1000 * 30,
  });
  const tariffState = tariffStateFromProject(projectQ.data);
  const runAllowed = canRunCheck(tariffState, 'tep');
  const runGateText = runBlockReason(tariffState, 'tep');

  const extract = async () => {
    try {
      setTariffErr(null);
      await api.post(`/projects/${projectId}/tep-registry/extract`, {});
      qc.invalidateQueries({ queryKey: ['tep-registry', projectId] });
    } catch (e) {
      // 15.E: тарифный гейт (402) — upsell, не сырая ошибка.
      const msg = tariffErrorMessage(e);
      setTariffErr(msg ?? (e instanceof Error ? e.message : 'Не удалось сформировать реестр'));
    }
  };

  const toggle = async (item: TepRegItem) => {
    setBusy(item.id);
    try {
      await api.post(`/projects/${projectId}/tep-registry/${item.id}/exclude?excluded=${!item.excluded}`, {});
      qc.invalidateQueries({ queryKey: ['tep-registry', projectId] });
    } finally {
      setBusy(null);
    }
  };

  const items = data?.items ?? [];
  const running = data?.status === 'running';
  const excludedCount = items.filter((i) => i.excluded).length;

  return (
    <div className="bg-white rounded-card border border-border" style={{ padding: 24, marginBottom: 20 }}>
      <div className="flex items-start" style={{ gap: 12, marginBottom: open ? 12 : 0 }}>
        <button type="button" onClick={() => setOpen(!open)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginTop: 2 }}>
          {open ? <ChevronDown size={18} stroke="#6B7280" /> : <ChevronRight size={18} stroke="#6B7280" />}
        </button>
        <Gauge size={20} stroke="#6B7280" style={{ marginTop: 2 }} />
        <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => setOpen(!open)}>
          <div className="text-ink" style={{ fontSize: 14, fontWeight: 600 }}>
            Настройка сверки: реестр показателей объекта
            {items.length > 0 && (
              <span className="text-muted" style={{ fontWeight: 400 }}>
                {' '}· {items.length} показателей{excludedCount ? ` (${excludedCount} исключено)` : ''}
              </span>
            )}
          </div>
          <div className="text-muted" style={{ fontSize: 12, lineHeight: 1.5, maxWidth: 660 }}>
            {open
              ? 'Список формируется из вашего проекта: таблица ТЭП в ПЗ + требования ТЗ/ТУ. Лишнее исключите — исключённые показатели не пойдут в сверку.'
              : 'Показатели берутся из ПЗ и задания вашего проекта, не из шаблона. Разверните, чтобы проверить состав.'}
          </div>
        </div>
        <button type="button" onClick={extract} disabled={running || !runAllowed}
          style={{ padding: '8px 16px', borderRadius: 20, fontSize: 12.5, fontWeight: 600,
                   background: (running || !runAllowed) ? '#E5E7EB' : '#0A1628',
                   color: (running || !runAllowed) ? '#6B7280' : '#FFF',
                   border: 'none', cursor: (running || !runAllowed) ? 'default' : 'pointer',
                   display: 'flex', alignItems: 'center', gap: 6 }}>
          <RefreshCw size={13} className={running ? 'animate-spin' : undefined} />
          {running ? 'Формирую…' : (items.length ? 'Переизвлечь показатели' : 'Сформировать реестр')}
        </button>
      </div>

      {!runAllowed && runGateText && <PayGateBanner projectId={projectId} reason={runGateText} />}
      {tariffErr && (
        <p style={{ color: '#B45309', fontSize: 12.5, margin: '0 0 10px', maxWidth: 720 }}>{tariffErr}</p>
      )}

      {data?.status === 'error' && (
        <div style={{ padding: 10, borderRadius: 8, background: 'rgba(239,68,68,.08)', color: '#B91C1C', fontSize: 12.5, marginBottom: 10 }}>
          Формирование упало: {data.error || 'причина не указана'}
        </div>
      )}

      {open && items.length === 0 && !running && (
        <div className="text-muted text-center" style={{ padding: '28px 16px', border: '1px dashed #E5E7EB', borderRadius: 10, fontSize: 12.5 }}>
          Реестр ещё не сформирован. Нажмите «Сформировать реестр» — показатели
          будут извлечены из таблицы ТЭП в ПЗ и требований ТЗ/ТУ вашего проекта.
        </div>
      )}

      {open && items.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
          {items.map((it) => (
            <div key={it.id} className="flex items-start"
                 style={{ gap: 10, padding: '9px 12px', border: '1px solid #F3F4F6', borderRadius: 8,
                          background: it.excluded ? '#FAFAFA' : '#FFF', opacity: it.excluded ? 0.55 : 1 }}>
              <div style={{ flex: 1, fontSize: 12.5, lineHeight: 1.5, color: '#1F2937',
                            textDecoration: it.excluded ? 'line-through' : 'none' }}>
                <span style={{ fontWeight: 600 }}>{it.name}</span>
                {it.unit && <span className="text-muted" style={{ fontSize: 11 }}> · {it.unit}</span>}
                <div className="text-muted" style={{ fontSize: 11.5, marginTop: 2 }}>
                  {it.req_value && (
                    <span style={{ marginRight: 12 }}>
                      требуется: {OP_LABEL[it.req_op || '='] || ''} {it.req_value}
                      {it.req_source && <> ({it.req_source}{it.req_item_no ? ` п. ${it.req_item_no}` : ''})</>}
                    </span>
                  )}
                  {it.declared_value && <span>в ПЗ заявлено: {it.declared_value}</span>}
                  {!it.req_value && !it.declared_value && <span>без исходного значения</span>}
                </div>
              </div>
              <button type="button" onClick={() => toggle(it)} disabled={busy === it.id}
                title={it.excluded ? 'Вернуть в сверку' : 'Исключить из сверки'}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', padding: 2 }}>
                {it.excluded ? <Eye size={15} /> : <EyeOff size={15} />}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
